import React, { useContext, useEffect, useState } from 'react';
import { fetchIncomeStatement, fetchIncomeStatementGrowth } from '../api';
import IncomeStatementChart from '../components/IncomeStatementChart';
import IncomeStatementTable from '../components/IncomeStatementTable';
import { StockVisualizationContext } from '../contexts/StockVisualizationContext';
import './StockVisualization.css';
import '../styles.css';

const TABLE_VIEW = {
  ABSOLUTE: 'incomeStatement',
  GROWTH: 'incomeStatementGrowth'
};

const MAX_SELECTED_METRICS = 5;

const DEFAULT_CHART_METRICS = [
  'revenue',
  'costOfRevenue',
  'grossProfit',
  'netIncome',
  'ebitda'
];

const METADATA_FIELDS = new Set([
  'date',
  'symbol',
  'reportedCurrency',
  'cik',
  'fillingDate',
  'filingDate',
  'acceptedDate',
  'calendarYear',
  'fiscalYear',
  'period',
  'link',
  'finalLink'
]);

const METRIC_LABELS = {
  revenue: 'Revenue',
  costOfRevenue: 'Cost of Revenue',
  grossProfit: 'Gross Profit',
  grossProfitRatio: 'Gross Profit Ratio',
  researchAndDevelopmentExpenses: 'R&D Expenses',
  generalAndAdministrativeExpenses: 'General & Administrative Expenses',
  sellingAndMarketingExpenses: 'Selling & Marketing Expenses',
  sellingGeneralAndAdministrativeExpenses: 'SG&A Expenses',
  otherExpenses: 'Other Expenses',
  operatingExpenses: 'Operating Expenses',
  costAndExpenses: 'Cost and Expenses',
  netInterestIncome: 'Net Interest Income',
  interestIncome: 'Interest Income',
  interestExpense: 'Interest Expense',
  depreciationAndAmortization: 'Depreciation & Amortization',
  ebitda: 'EBITDA',
  ebitdaratio: 'EBITDA Ratio',
  ebit: 'EBIT',
  nonOperatingIncomeExcludingInterest: 'Non-Operating Income Ex. Interest',
  operatingIncome: 'Operating Income',
  operatingIncomeRatio: 'Operating Income Ratio',
  totalOtherIncomeExpensesNet: 'Other Income/Expenses Net',
  incomeBeforeTax: 'Income Before Tax',
  incomeBeforeTaxRatio: 'Income Before Tax Ratio',
  incomeTaxExpense: 'Income Tax Expense',
  netIncomeFromContinuingOperations: 'Net Income From Continuing Ops',
  netIncomeFromDiscontinuedOperations: 'Net Income From Discontinued Ops',
  otherAdjustmentsToNetIncome: 'Other Adjustments To Net Income',
  netIncome: 'Net Income',
  netIncomeDeductions: 'Net Income Deductions',
  bottomLineNetIncome: 'Bottom Line Net Income',
  netIncomeRatio: 'Net Income Ratio',
  eps: 'EPS',
  epsDiluted: 'Diluted EPS',
  weightedAverageShsOut: 'Weighted Average Shares Out',
  weightedAverageShsOutDil: 'Weighted Average Shares Out Diluted'
};

const formatMetricLabel = (metricKey) =>
  METRIC_LABELS[metricKey] ||
  metricKey
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const toChartData = (rows) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows
    .map((row) => {
      const chartRow = { fiscalYear: row.fiscalYear };

      Object.entries(row).forEach(([key, value]) => {
        if (!METADATA_FIELDS.has(key) && typeof value === 'number' && Number.isFinite(value)) {
          chartRow[key] = value;
        }
      });

      return chartRow;
    })
    .filter((row) => row.fiscalYear)
    .sort((a, b) => Number(a.fiscalYear) - Number(b.fiscalYear));
};

const hasMetricData = (chartData, metricKey) =>
  chartData.some((row) => typeof row[metricKey] === 'number' && Number.isFinite(row[metricKey]));

export default function IncomeStatement() {
  const { selectedStock } = useContext(StockVisualizationContext);
  const [incomeStatementRows, setIncomeStatementRows] = useState([]);
  const [incomeStatementGrowthRows, setIncomeStatementGrowthRows] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [metricPickerNotice, setMetricPickerNotice] = useState('');
  const [isMetricLimitTooltipVisible, setIsMetricLimitTooltipVisible] = useState(false);
  const [tableView, setTableView] = useState(TABLE_VIEW.ABSOLUTE);
  const [isLoading, setIsLoading] = useState(false);
  const [incomeStatementError, setIncomeStatementError] = useState('');
  const [incomeStatementGrowthError, setIncomeStatementGrowthError] = useState('');

  useEffect(() => {
    let isCancelled = false;

    const loadIncomeStatement = async () => {
      if (!selectedStock) {
        setIncomeStatementRows([]);
        setIncomeStatementGrowthRows([]);
        setChartData([]);
        setIncomeStatementError('');
        setIncomeStatementGrowthError('');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setIncomeStatementError('');
      setIncomeStatementGrowthError('');

      const [incomeStatementResult, incomeStatementGrowthResult] = await Promise.allSettled([
        fetchIncomeStatement(selectedStock),
        fetchIncomeStatementGrowth(selectedStock)
      ]);

      if (isCancelled) {
        return;
      }

      if (incomeStatementResult.status === 'fulfilled') {
        const incomeStatement = Array.isArray(incomeStatementResult.value)
          ? incomeStatementResult.value
          : [];
        setIncomeStatementRows(incomeStatement);
        setChartData(toChartData(incomeStatement));
      } else {
        console.error('Error fetching income statement data:', incomeStatementResult.reason);
        setIncomeStatementRows([]);
        setChartData([]);
        setIncomeStatementError(`Unable to load income statement data for ${selectedStock}.`);
      }

      if (incomeStatementGrowthResult.status === 'fulfilled') {
        setIncomeStatementGrowthRows(
          Array.isArray(incomeStatementGrowthResult.value)
            ? incomeStatementGrowthResult.value
            : []
        );
      } else {
        console.error(
          'Error fetching income statement growth data:',
          incomeStatementGrowthResult.reason
        );
        setIncomeStatementGrowthRows([]);
        setIncomeStatementGrowthError(
          `Unable to load income statement growth data for ${selectedStock}.`
        );
      }

      setIsLoading(false);
    };

    loadIncomeStatement();

    return () => {
      isCancelled = true;
    };
  }, [selectedStock]);

  useEffect(() => {
    const metricKeys = new Set();

    chartData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key !== 'fiscalYear' && hasMetricData(chartData, key)) {
          metricKeys.add(key);
        }
      });
    });

    const metricsWithData = Array.from(metricKeys)
      .map((key) => ({ key, label: formatMetricLabel(key) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const defaultSelectedMetrics = DEFAULT_CHART_METRICS.filter((key) => metricKeys.has(key));
    const fallbackSelectedMetrics = metricsWithData
      .map(({ key }) => key)
      .filter((key) => !defaultSelectedMetrics.includes(key));

    setAvailableMetrics(metricsWithData);
    setSelectedMetrics(
      [...defaultSelectedMetrics, ...fallbackSelectedMetrics].slice(0, MAX_SELECTED_METRICS)
    );
    setMetricPickerNotice('');
  }, [chartData]);

  const handleMetricSelect = (event) => {
    const metricKey = event.target.value;

    if (!metricKey) {
      return;
    }

    setMetricPickerNotice('');

    setSelectedMetrics((currentMetrics) => {
      if (currentMetrics.includes(metricKey)) {
        return currentMetrics;
      }

      if (currentMetrics.length >= MAX_SELECTED_METRICS) {
        setMetricPickerNotice(`Select up to ${MAX_SELECTED_METRICS} metrics.`);
        return currentMetrics;
      }

      return [...currentMetrics, metricKey];
    });

    event.target.value = '';
  };

  const removeSelectedMetric = (metricKey) => {
    setMetricPickerNotice('');
    setSelectedMetrics((currentMetrics) => currentMetrics.filter((key) => key !== metricKey));
  };

  const activeTableRows =
    tableView === TABLE_VIEW.GROWTH ? incomeStatementGrowthRows : incomeStatementRows;
  const activeTableError =
    tableView === TABLE_VIEW.GROWTH
      ? incomeStatementGrowthError
      : incomeStatementError;
  const unselectedAvailableMetrics = availableMetrics.filter(
    ({ key }) => !selectedMetrics.includes(key)
  );
  const isMetricSelectDisabled =
    selectedMetrics.length >= MAX_SELECTED_METRICS || !unselectedAvailableMetrics.length;
  const showMetricLimitTooltip = selectedMetrics.length >= MAX_SELECTED_METRICS;

  return (
    <div className="App income-statement-page">
      <div className="main-container">
        <div className="content-area income-statement-content-area">
          <h2>Income Statement</h2>
          <p>Selected stock: {selectedStock}</p>
          <div className="income-statement-page-section">
            {isLoading ? <p>Loading income statement data...</p> : null}
            {!isLoading && incomeStatementError ? <p>{incomeStatementError}</p> : null}
            {!isLoading && !incomeStatementError ? (
              <div className="income-statement-chart-section">
                {availableMetrics.length ? (
                  <div className="income-statement-metric-picker" aria-label="Income statement chart metrics">
                    <div className="income-statement-selected-metrics">
                      {selectedMetrics.map((metricKey) => {
                        const metric = availableMetrics.find(({ key }) => key === metricKey);

                        if (!metric) {
                          return null;
                        }

                        return (
                          <button
                            key={metric.key}
                            type="button"
                            onClick={() => removeSelectedMetric(metric.key)}
                            className="income-statement-selected-metric-chip"
                            aria-label={`Remove ${metric.label}`}
                          >
                            {metric.label}
                            <span aria-hidden="true">x</span>
                          </button>
                        );
                      })}
                    </div>
                    <div
                      className="income-statement-metric-select-row"
                      onMouseEnter={() => setIsMetricLimitTooltipVisible(true)}
                      onMouseLeave={() => setIsMetricLimitTooltipVisible(false)}
                      onFocus={() => setIsMetricLimitTooltipVisible(true)}
                      onBlur={() => setIsMetricLimitTooltipVisible(false)}
                    >
                      <select
                        aria-label="Add income statement chart metric"
                        disabled={isMetricSelectDisabled}
                        onChange={handleMetricSelect}
                        defaultValue=""
                        className="income-statement-metric-select"
                      >
                        <option value="" disabled>
                          Add up to 5 metrics
                        </option>
                        {unselectedAvailableMetrics.map((metric) => (
                          <option key={metric.key} value={metric.key}>
                            {metric.label}
                          </option>
                        ))}
                      </select>
                      {showMetricLimitTooltip ? (
                        <span
                          className={`income-statement-metric-limit-tooltip ${
                            isMetricLimitTooltipVisible ? 'is-visible' : ''
                          }`}
                          role="tooltip"
                        >
                          To add different metrics, remove current metrics
                        </span>
                      ) : null}
                    </div>
                    {metricPickerNotice ? <p>{metricPickerNotice}</p> : null}
                  </div>
                ) : null}
                <IncomeStatementChart
                  chartData={chartData}
                  metricLabels={Object.fromEntries(
                    availableMetrics.map(({ key, label }) => [key, label])
                  )}
                  selectedMetrics={selectedMetrics}
                  selectedStock={selectedStock}
                />
              </div>
            ) : null}
            {!isLoading && selectedStock ? (
              <div className="income-statement-table-section">
                <div
                  className="income-statement-toggle-row"
                  role="tablist"
                  aria-label="Income statement table view"
                >
                  <div className="income-statement-toggle-group">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tableView === TABLE_VIEW.ABSOLUTE}
                      onClick={() => setTableView(TABLE_VIEW.ABSOLUTE)}
                      className={`income-statement-toggle-pill ${
                        tableView === TABLE_VIEW.ABSOLUTE ? 'is-active' : ''
                      }`}
                    >
                      Income statement
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tableView === TABLE_VIEW.GROWTH}
                      onClick={() => setTableView(TABLE_VIEW.GROWTH)}
                      className={`income-statement-toggle-pill ${
                        tableView === TABLE_VIEW.GROWTH ? 'is-active' : ''
                      }`}
                    >
                      Income statement growth
                    </button>
                  </div>
                </div>
                {activeTableError ? <p>{activeTableError}</p> : null}
                {!activeTableError ? (
                  <IncomeStatementTable
                    rows={activeTableRows}
                    selectedStock={selectedStock}
                    tableView={tableView}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
